import {
	GenericLevels, SuperGridPluginParams,
	useSupergridMutations,
	VerticalSuperGridColDef,
} from '@aleph/supergrid';
import { useUrlParams } from '@aleph/shared-components';
import { create } from 'zustand';
import { isEqual, isFunction } from 'lodash';
import { useEffect, useState } from 'react';
import {
	CellValueChangedEvent, GridApi, UndoStartedEvent, RedoStartedEvent,
} from 'ag-grid-community';
import { useNavigate, useParams } from 'react-router-dom';

type UndoRedoEvent = {
	hash: string;
	field: string;
	oldValue: any;
	newValue: any;
	column: string;
	level: string,
	event: CellValueChangedEvent
};

type UseUndoRedoEvents = {
	undoEvents: UndoRedoEvent[];
	redoEvents: UndoRedoEvent[];
	setUndoEvents: (events: UndoRedoEvent[] | ((events: UndoRedoEvent[]) => UndoRedoEvent[])) => void;
	setRedoEvents: (events: UndoRedoEvent[] | ((events: UndoRedoEvent[]) => UndoRedoEvent[])) => void;
};

const useUndoRedoEvents = create<UseUndoRedoEvents>((set) => ({
	undoEvents:    [],
	redoEvents:    [],
	setUndoEvents: (events: UndoRedoEvent[] | ((events: UndoRedoEvent[]) => UndoRedoEvent[])) => set(
		curr => ({ undoEvents: isFunction(events) ? events(curr?.undoEvents) : events }),
	),
	setRedoEvents: (events: UndoRedoEvent[] | ((events: UndoRedoEvent[]) => UndoRedoEvent[])) => set(
		curr => ({ redoEvents: isFunction(events) ? events(curr?.redoEvents) : events }),
	),
}));

const focusEventCell = (e: UndoRedoEvent, api: GridApi, isVertical: boolean) => {
	if (isVertical) {
		api?.forEachNode((node) => {
			if (node?.data?.field === e.field) {
				api?.clearRangeSelection();
				api?.setFocusedCell(node.rowIndex ?? -1, e.column);
				api?.addCellRange({
					rowStartIndex: node.rowIndex,
					rowEndIndex:   node.rowIndex,
					columns:       [e.column],
				});
			}
		});
	} else {
		api?.forEachNode((node) => {
			if (node?.data?.hash === e.hash) {
				api?.clearRangeSelection();
				api?.setFocusedCell(node.rowIndex ?? -1, e.field);
				api?.addCellRange({
					rowStartIndex: node.rowIndex,
					rowEndIndex:   node.rowIndex,
					columns:       [e.field],
				});
			}
		});
	}
};

const useMutationsUndoRedoPlugin = ({ gridApi }: Pick<SuperGridPluginParams, 'gridApi'>) => {
	const { changeMutation } = useSupergridMutations();
	const navigate = useNavigate();

	const { setUrlParam, params } = useUrlParams({ navigate, useParams });
	const [stagedEvent, setStagedEvent] = useState<{
		event: UndoRedoEvent,
		type: 'undo' | 'redo',
	} | null>(null);

	const {
		undoEvents, redoEvents, setUndoEvents, setRedoEvents,
	} = useUndoRedoEvents();

	const undoEvent = (event: UndoRedoEvent, api: GridApi, isVertical: boolean) => {
		focusEventCell(event, api, isVertical);
		changeMutation({
			hash:          event.hash,
			[event.field]: event.oldValue,
		});
		setUndoEvents(events => events.filter(curr => !isEqual(curr, event)));
		setRedoEvents(events => [...events, event]);
	};

	const redoEvent = (event: UndoRedoEvent, api: GridApi, isVertical: boolean) => {
		focusEventCell(event, api, isVertical);
		changeMutation({
			hash:          event.hash,
			[event.field]: event.newValue,
		});
		setRedoEvents(curr => curr.filter(e => !isEqual(e, event)));
		setUndoEvents(events => [...events, event]);
	};

	// this is used to do the undo event & focus the appropriate cell after the vertical level is changed.
	useEffect(() => {
		const gridLevel = gridApi?.getRenderedNodes()?.[0]?.data?.level;

		if (stagedEvent && gridLevel === stagedEvent?.event?.level && gridApi) {
			if (stagedEvent.type === 'undo') {
				undoEvent(stagedEvent.event, gridApi, true);
			} else {
				redoEvent(stagedEvent.event, gridApi, true);
			}

			setStagedEvent(null);
		}
	}, [params?.creationLevel, gridApi]);

	return {
		onUndoStarted(e: UndoStartedEvent) {
			const undoEventsCopy = [...undoEvents];
			const lastEvent = undoEventsCopy.pop();

			if (lastEvent) {
				if (e.context?.isVertical && params?.creationLevel !== lastEvent.level) {
					setUrlParam('creationLevel', lastEvent.level);
					setStagedEvent({
						event: lastEvent,
						type:  'undo',
					});

					return;
				}

				undoEvent(lastEvent, e.api, !!e.context?.isVertical);
			}
		},
		onRedoStarted(e: RedoStartedEvent) {
			const redoEventsCopy = [...redoEvents];
			const lastEvent = redoEventsCopy.pop();

			if (lastEvent) {
				if (e.context?.isVertical && params?.creationLevel !== lastEvent.level) {
					setUrlParam('creationLevel', lastEvent.level);
					setStagedEvent({
						event: lastEvent,
						type:  'redo',
					});

					return;
				}

				redoEvent(lastEvent, e.api, !!e.context?.isVertical);
			}
		},
		onCellValueChanged: (e: CellValueChangedEvent) => {
			const {
				newValue,
				oldValue,
				colDef,
				data,
				node,
			} = e;

			if (newValue === oldValue) {
				return;
			}

			const entityData = (colDef as VerticalSuperGridColDef<GenericLevels>)?.entityData ?? data;
			const field = data?.field ?? (colDef as VerticalSuperGridColDef<GenericLevels>)?.field ?? '';

			if (!entityData?.level || (!entityData?.id && !entityData?.hash) || !colDef?.field) {
				return;
			}

			setRedoEvents([]);
			setUndoEvents(events => [...events, {
				oldValue,
				newValue,
				field,
				level:  entityData.level,
				hash:   entityData.hash,
				column: colDef?.field ?? '',
				node,
				event:  e,
			}]);
		},
	};
};

export default useMutationsUndoRedoPlugin;
